// Phase-1 Touchpoints — REST client.
//
// Talks to the manual Touchpoint surface (BE Phase-1):
//
//   * GET  /api/v1/accounts/{account_id}/touchpoints/
//   * POST /api/v1/accounts/{account_id}/touchpoints/
//   * GET  /api/v1/touchpoints/{id}
//   * PATCH /api/v1/touchpoints/{id}
//   * POST /api/v1/touchpoints/{id}/{complete|cancel|no_show|delete}
//   * GET  /api/v1/dashboard/touchpoints  (broker stats)

import { api } from "@/api/client";

import type {
  RecordingPlaybackUrlResponse,
  Touchpoint,
  TouchpointCreateInput,
  TouchpointListResponse,
  TouchpointRecording,
  TouchpointRecordingListResponse,
  TouchpointStatsResponse,
  TouchpointUpdateInput,
} from "./types";


function _accountBase(accountId: string): string {
  return `/api/v1/accounts/${encodeURIComponent(accountId)}/touchpoints/`;
}


function _tpBase(touchpointId: string): string {
  return `/api/v1/touchpoints/${encodeURIComponent(touchpointId)}`;
}


export function fetchAccountTouchpoints(
  accountId: string,
  opts?: { signal?: AbortSignal },
): Promise<TouchpointListResponse> {
  return api.get<TouchpointListResponse>(_accountBase(accountId), opts);
}


export function createAccountTouchpoint(
  accountId: string, input: TouchpointCreateInput,
): Promise<Touchpoint> {
  return api.post<Touchpoint>(_accountBase(accountId), input);
}


export function fetchTouchpoint(
  touchpointId: string,
  opts?: { signal?: AbortSignal },
): Promise<Touchpoint> {
  return api.get<Touchpoint>(_tpBase(touchpointId), opts);
}


export function updateTouchpoint(
  touchpointId: string, input: TouchpointUpdateInput,
): Promise<Touchpoint> {
  return api.patch<Touchpoint>(_tpBase(touchpointId), input);
}


export function completeTouchpoint(
  touchpointId: string,
  body?: { outcome_summary?: string; occurred_at?: string },
): Promise<Touchpoint> {
  return api.post<Touchpoint>(`${_tpBase(touchpointId)}/complete`, body ?? {});
}


export function cancelTouchpoint(
  touchpointId: string, body?: { reason?: string },
): Promise<Touchpoint> {
  return api.post<Touchpoint>(`${_tpBase(touchpointId)}/cancel`, body ?? {});
}


export function markTouchpointNoShow(
  touchpointId: string,
): Promise<Touchpoint> {
  return api.post<Touchpoint>(`${_tpBase(touchpointId)}/no_show`, {});
}


export function deleteTouchpoint(
  touchpointId: string,
): Promise<{ touchpoint_id: string; deleted: boolean }> {
  return api.post<{ touchpoint_id: string; deleted: boolean }>(
    `${_tpBase(touchpointId)}/delete`, {},
  );
}


// ── Recordings (Phase-1 chunk 2) ────────────────────────────────────────


export function fetchTouchpointRecordings(
  touchpointId: string,
  opts?: { signal?: AbortSignal },
): Promise<TouchpointRecordingListResponse> {
  return api.get<TouchpointRecordingListResponse>(
    `${_tpBase(touchpointId)}/recordings`,
    opts,
  );
}


export function uploadTouchpointRecording(
  touchpointId: string, file: File,
): Promise<TouchpointRecording> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return api.post<TouchpointRecording>(
    `${_tpBase(touchpointId)}/recordings`,
    fd,
  );
}


export function deleteTouchpointRecording(
  recordingId: string,
): Promise<{ recording_id: string; deleted: boolean }> {
  return api.delete<{ recording_id: string; deleted: boolean }>(
    `/api/v1/recordings/${encodeURIComponent(recordingId)}`,
  );
}


export function fetchRecordingPlaybackUrl(
  recordingId: string,
): Promise<RecordingPlaybackUrlResponse> {
  return api.get<RecordingPlaybackUrlResponse>(
    `/api/v1/recordings/${encodeURIComponent(recordingId)}/playback-url`,
  );
}


export function fetchTouchpointStats(
  opts?: { signal?: AbortSignal; days?: number },
): Promise<TouchpointStatsResponse> {
  const days = opts?.days;
  const path = days
    ? `/api/v1/dashboard/touchpoints?days=${days}`
    : "/api/v1/dashboard/touchpoints";
  return api.get<TouchpointStatsResponse>(path, { signal: opts?.signal });
}
